import { GroupBase, chakraComponents } from "chakra-react-select";
import V2Image from "components/V2Image";
import {
  AsyncSmartSelect,
  AsyncSmartSelectProps,
  defaultOptionStyles,
} from "components/SmartSelect";
import { fancyBearsSupply } from "config";
import useTranslate from "hooks/useTranslate";

type V2SelectOption = {
  label: string;
  value?: number;
  options?: V2SelectOption[];
};

type V2SelectProps = Omit<
  AsyncSmartSelectProps<V2SelectOption, false, GroupBase<V2SelectOption>>,
  | "onChange"
  | "value"
  | "defaultOptions"
  | "loadOptions"
  | "components"
  | "blurInputOnSelect"
> & {
  tokenId?: number;
  ownedTokens?: number[];
  allTokens?: number[];
  firstTokenId?: number;
  lastTokenId?: number;
  optionPrefix?: string;
  onChange?: (tokenId?: number) => void;
};

const formatAndLimitOptions = (
  options: (number | string)[],
  optionPrefix: string
) =>
  options.slice(0, 6).map((tokenId) => ({
    label: `${optionPrefix}${tokenId}`,
    value: Number(tokenId),
  }));

const generateOptions = (
  inputValue: string = "",
  allTokens: number[],
  optionPrefix: string
): V2SelectOption[] => {
  if (!inputValue) {
    return formatAndLimitOptions(allTokens, optionPrefix);
  }

  const filteredTokens = allTokens.filter(
    (tokenId) =>
      tokenId.toString().startsWith(inputValue) &&
      tokenId.toString() !== inputValue
  );

  return formatAndLimitOptions(
    filteredTokens.length > 1 ? filteredTokens : allTokens,
    optionPrefix
  );
};

const V2Select = ({
  tokenId,
  ownedTokens = [],
  allTokens = [],
  firstTokenId = 0,
  lastTokenId = fancyBearsSupply,
  optionPrefix = "#",
  chakraStyles = {},
  onChange,
  ...rest
}: V2SelectProps) => {
  const translate = useTranslate();

  const defaultOptions = [
    {
      label: translate("v2Select:owned"),
      options: ownedTokens
        .map((tokenId) => ({
          label: `${optionPrefix}${tokenId}`,
          value: tokenId,
        }))
        .sort((a, b) => a.value - b.value),
    },
    {
      label: ownedTokens.length
        ? translate("v2Select:other")
        : translate("v2Select:community"),
      options: generateOptions(
        tokenId?.toString(),
        allTokens,
        optionPrefix
      ).filter((option) => option.value !== tokenId),
    },
  ];

  return (
    <AsyncSmartSelect<V2SelectOption, false, GroupBase<V2SelectOption>>
      value={
        tokenId !== undefined
          ? { value: tokenId, label: `#${tokenId}` }
          : undefined
      }
      placeholder={translate("v2Select:placeholder")}
      components={{
        Option: ({ children, ...props }) => (
          <chakraComponents.Option {...props}>
            <V2Image
              tokenId={props.data.value!}
              size="256"
              w="8"
              h="8"
              flex="none"
              mr="3"
              borderRadius="full"
            />

            {children}
          </chakraComponents.Option>
        ),
        Input: ({ children, type, ...props }) => (
          <chakraComponents.Input
            type="number"
            min={firstTokenId}
            max={lastTokenId}
            {...props}
          />
        ),
      }}
      chakraStyles={{
        option: (provided) => ({
          ...provided,
          ...defaultOptionStyles,
          px: "3",
          py: "1.5",
        }),
        ...chakraStyles,
      }}
      defaultOptions={defaultOptions}
      loadOptions={(inputValue: string) =>
        Promise.resolve(generateOptions(inputValue, allTokens, optionPrefix))
      }
      onChange={(option) => onChange && onChange(option?.value)}
      blurInputOnSelect
      {...rest}
    />
  );
};

export default V2Select;
